import {
    ArrowBack,
    CheckCircleOutlined,
    Download,
    Draw,
    RadioButtonUnchecked,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import Contract from 'components/common/AgreementSignature/Contract';
import Loader from 'components/v2/common/loader';
import SignatureBoard from './signatureBoard';

import useTranslate from 'hooks/useTranslate';
import type { AgreementDocumentInterface, ApplicationRetainer } from 'types';
import { isAgreementSigningAvailable, isAgreementSigningBlocked } from 'utils/agreement';
import { downloadBlob } from 'utils/file';

interface RetainerDocumentPanelProps {
    retainer: ApplicationRetainer | null;
    document: AgreementDocumentInterface | null;
    title: string;
    loading: boolean;
    /** Case candidate only — spouses can view but not sign (API returns 403). */
    canSign: boolean;
    isSubmitting: boolean;
    /** Sign endpoint is rate limited (1/min per case); disables the CTA after a 429. */
    rateLimited: boolean;
    onBack: () => void;
    onSign: (signature: string) => void;
}

const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const RetainerDocumentPanel: React.FC<RetainerDocumentPanelProps> = ({
    retainer,
    document,
    title,
    loading,
    canSign,
    isSubmitting,
    rateLimited,
    onBack,
    onSign,
}) => {
    const { translate } = useTranslate();
    const [signatureOpen, setSignatureOpen] = useState(false);

    const signed = Boolean(retainer?.signed);
    const signingBlocked = isAgreementSigningBlocked(retainer);
    const signedOn = formatDate(retainer?.signed_at);
    const showSign =
        !loading && retainer != null && canSign && isAgreementSigningAvailable(retainer);
    const signDisabled = isSubmitting || rateLimited;

    useEffect(() => {
        if (signed || signingBlocked) setSignatureOpen(false);
    }, [signed, signingBlocked]);

    const handleDownload = () => {
        if (!document?.file) return;
        downloadBlob(document.file, `retainer_${retainer?.agreement_id ?? ''}.pdf`);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '70vh',
                position: 'relative',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(248, 250, 252, 0.7)',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <IconButton
                        size="small"
                        onClick={onBack}
                        disabled={isSubmitting}
                        aria-label={translate('back')}
                    >
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                            noWrap
                        >
                            {title}
                        </Typography>
                        {retainer ? (
                            <Typography variant="caption" color="text.secondary">
                                {`#${retainer.agreement_id}`}
                            </Typography>
                        ) : null}
                    </Box>
                    {retainer ? (
                        <Chip
                            size="small"
                            icon={
                                signed ? (
                                    <CheckCircleOutlined sx={{ fontSize: 16 }} />
                                ) : (
                                    <RadioButtonUnchecked sx={{ fontSize: 16 }} />
                                )
                            }
                            label={
                                signed
                                    ? signedOn
                                        ? `${translate('retainerStatusSigned')} · ${signedOn}`
                                        : translate('retainerStatusSigned')
                                    : signingBlocked
                                      ? translate('retainerSigningBlocked')
                                      : translate('awaitingSignature')
                            }
                            color={signed ? 'success' : signingBlocked ? 'warning' : 'default'}
                            variant={signed ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600, ml: 1 }}
                        />
                    ) : null}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                    {document?.file ? (
                        <Tooltip title={translate('download')}>
                            <IconButton
                                size="small"
                                onClick={handleDownload}
                                aria-label={translate('download')}
                            >
                                <Download fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    {showSign ? (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={
                                isSubmitting ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <Draw fontSize="small" />
                                )
                            }
                            disabled={signDisabled}
                            onClick={() => setSignatureOpen(true)}
                            sx={{
                                textTransform: 'none',
                                color: '#fff',
                                '&:hover': { color: '#fff' },
                                '&.MuiButton-contained': { color: '#fff' },
                            }}
                        >
                            {translate('signAgreement')}
                        </Button>
                    ) : null}
                </Stack>
            </Stack>

            {!loading && retainer && signingBlocked ? (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                    {translate('retainerSigningBlockedClientDescription')}
                </Alert>
            ) : null}
            {!loading && retainer && !signed && !signingBlocked && !canSign ? (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                    {translate('onlyCandidateCanSign')}
                </Alert>
            ) : null}
            {rateLimited && !signed ? (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    {translate('signRateLimited')}
                </Alert>
            ) : null}

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {loading || !document ? (
                    <Loader rootSx={{ minHeight: 300 }} message={translate('loading')} />
                ) : (
                    <Contract
                        file={document.file}
                        signature={null}
                        signaturePosition={null}
                        onSignedAgreement={() => undefined}
                    />
                )}
            </Box>

            {signatureOpen ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2,
                        overflow: 'auto',
                    }}
                >
                    <SignatureBoard
                        isSubmitting={isSubmitting}
                        disabled={rateLimited}
                        onClose={() => {
                            if (isSubmitting) return;
                            setSignatureOpen(false);
                        }}
                        onSign={onSign}
                    />
                </Box>
            ) : null}
        </Paper>
    );
};
